/**
 * Parameters for fetching Mapbox directions
 */
export type GetMapboxDirectionsParams = {
  /**
   * Mapbox access token
   */
  accessToken: string;
  /**
   * Origin coordinates [lat, lng]
   */
  origin: [number, number];
  /**
   * Destination coordinates [lat, lng]
   */
  destination: [number, number];
  /**
   * Travel profile (default: walking)
   */
  profile?: "walking" | "cycling" | "driving";
};

/**
 * Result from Mapbox Directions API
 */
export type DirectionsResult = {
  /**
   * Route as GeoJSON LineString feature
   */
  route: GeoJSON.Feature<GeoJSON.LineString>;
  /**
   * Duration in seconds
   */
  duration: number;
  /**
   * Distance in meters
   */
  distance: number;
};

/**
 * Fetches walking/cycling/driving directions from Mapbox Directions API
 * @param params - Configuration parameters for the directions request
 * @returns DirectionsResult with route GeoJSON, or null if failed
 */
export const getMapboxDirections = async ({
  accessToken,
  origin,
  destination,
  profile = "walking",
}: GetMapboxDirectionsParams): Promise<DirectionsResult | null> => {
  try {
    // Mapbox expects [lng, lat] order
    const coordinates = `${origin[1]},${origin[0]};${destination[1]},${destination[0]}`;
    const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coordinates}?geometries=geojson&access_token=${accessToken}`;

    const response = await fetch(url);
    const data = await response.json();

    if (data.routes && data.routes.length > 0) {
      const route = data.routes[0];
      return {
        route: {
          type: "Feature",
          properties: {},
          geometry: route.geometry,
        },
        duration: route.duration,
        distance: route.distance,
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching directions:", error);
    return null;
  }
};
